import type { ComponentType } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { navLinks, site } from '@/content/site'
import { BookingButton } from '@/components/booking/BookingButton'
import {
  FacebookIcon,
  InstagramIcon,
  TiktokIcon,
  YelpIcon,
} from '@/components/icons/SocialIcons'
import { cn } from '@/lib/utils'
import { useFocusTrap } from '@/lib/useFocusTrap'

type HeaderSocial = {
  href: string
  label: string
  Icon: ComponentType<{ className?: string }>
}

const headerSocialLinks: readonly HeaderSocial[] = [
  { href: site.social.instagram, label: 'Instagram', Icon: InstagramIcon },
  { href: site.social.facebook, label: 'Facebook', Icon: FacebookIcon },
  { href: site.social.tiktok, label: 'TikTok', Icon: TiktokIcon },
  { href: site.social.yelp, label: 'Yelp', Icon: YelpIcon },
]

function navIsActive(pathname: string, currentHash: string, to: string): boolean {
  const [path, hash] = to.split('#')
  if (pathname !== path) return false
  if (!hash) return true
  return currentHash === `#${hash}`
}

export function Header() {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const menuToggleRef = useRef<HTMLButtonElement>(null)
  const reduced = useReducedMotion()
  const location = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useFocusTrap({
    active: open,
    containerRef: panelRef,
    onEscape: () => setOpen(false),
    returnFocusRef: menuToggleRef,
  })

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  const linkClass = (to: string) => {
    const active = navIsActive(location.pathname, location.hash, to)
    return cn(
      'px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] transition-opacity duration-200',
      scrolled
        ? active
          ? 'text-on-olive'
          : 'text-on-olive-muted hover:text-on-olive'
        : active
          ? 'text-ink'
          : 'text-ink/75 hover:text-ink'
    )
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: reduced ? 'instant' : 'smooth' })
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-50 transition-[background-color,backdrop-filter,border-color,box-shadow] duration-300',
        scrolled
          ? 'border-b border-olive-border bg-olive/97 shadow-[var(--shadow-olive)] backdrop-blur-md'
          : 'border-b border-transparent bg-transparent'
      )}
    >
      {open && (
        <motion.button
          type="button"
          className="fixed inset-0 z-40 min-[900px]:hidden cursor-pointer border-0 bg-ink/45 p-0"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        />
      )}

      <div className="relative z-50 mx-auto flex h-[4.25rem] max-w-[1280px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <motion.div whileHover={reduced ? undefined : { opacity: 0.85 }} transition={{ duration: 0.15 }}>
          <Link
            to="/"
            className={cn(
              'flex items-center font-display text-lg font-semibold tracking-tight sm:text-xl',
              scrolled ? 'text-on-olive' : 'text-ink'
            )}
            onClick={scrollToTop}
          >
            {site.name}
          </Link>
        </motion.div>

        <nav
          className="hidden min-[900px]:flex min-[900px]:flex-1 min-[900px]:justify-center min-[900px]:gap-1 lg:gap-2"
          aria-label="Primary"
        >
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              className={() => linkClass(l.to)}
              onClick={scrollToTop}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden min-[900px]:flex min-[900px]:items-center">
            <BookingButton
              variant={scrolled ? 'onDark' : 'primary'}
              className="min-h-10 min-[900px]:min-h-11"
            >
              Book now
            </BookingButton>
          </div>
          <div className="hidden items-center gap-0.5 sm:flex">
            {headerSocialLinks.map(({ href, label, Icon }) => (
              <a
                key={label}
                href={href}
                className={cn(
                  'inline-flex min-h-11 min-w-11 items-center justify-center transition-opacity hover:opacity-100',
                  scrolled ? 'text-on-olive-soft opacity-90' : 'text-ink opacity-80'
                )}
                rel="noopener noreferrer"
                target="_blank"
                aria-label={label}
              >
                <Icon />
              </a>
            ))}
          </div>
          <motion.button
            ref={menuToggleRef}
            type="button"
            data-mobile-nav-toggle
            className={cn(
              'inline-flex min-h-11 min-w-11 items-center justify-center rounded-[var(--radius-button)] border min-[900px]:hidden',
              scrolled
                ? 'border-olive-border bg-olive-elevated text-on-olive'
                : 'border-hairline bg-canvas/90 text-ink'
            )}
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((v) => !v)}
            whileTap={reduced ? undefined : { scale: 0.97 }}
          >
            <span className="sr-only">{open ? 'Close menu' : 'Open menu'}</span>
            {open ? (
              <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
                <path
                  fill="currentColor"
                  d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L12 13.41l-6.29 6.3-1.42-1.41L10.59 12 4.3 5.71 5.71 4.3 12 10.59l6.29-6.3z"
                />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
                <path fill="currentColor" d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
              </svg>
            )}
          </motion.button>
        </div>
      </div>

      {open && (
        <motion.div
          id="mobile-nav"
          ref={panelRef}
          className="relative z-50 border-t border-olive-border bg-olive px-4 py-5 min-[900px]:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
          initial={reduced ? false : { opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="mx-auto flex max-w-[1280px] flex-col gap-0.5">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                className={() =>
                  cn(
                    'px-3 py-3 text-xs font-semibold uppercase tracking-[0.16em]',
                    navIsActive(location.pathname, location.hash, l.to)
                      ? 'bg-olive-elevated text-on-olive'
                      : 'text-on-olive-muted hover:text-on-olive'
                  )
                }
                onClick={() => {
                  scrollToTop()
                  setOpen(false)
                }}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="mt-6 flex flex-wrap items-center gap-1 border-t border-olive-border pt-4">
              {headerSocialLinks.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  className="inline-flex min-h-11 min-w-11 items-center justify-center text-on-olive-soft opacity-90 transition-opacity hover:text-on-olive"
                  rel="noopener noreferrer"
                  target="_blank"
                  aria-label={label}
                  onClick={() => setOpen(false)}
                >
                  <Icon />
                </a>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </header>
  )
}
